// THE SOCIAL LAYER — everything that simulates other players.
//
// One file on purpose. Together with social.ts, fakeChips.ts and social.css
// this is the whole layer, and it is built to be removable in one commit:
// delete these four files, delete the import in Page.tsx, and neither
// roulette.css nor any money path is touched. See
// docs/ROULETTE_UI_V2_SPEC.md §12 for why that matters.
//
// NOTHING HERE IMPORTS ./engine, calls makeBet(), or holds a reference to
// place(). The only thing it borrows from the real game is geometry.
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildTargets, launchPoint, pickDenom, pickTarget, type FakeTarget,
  EMIT_MAX_MS, EMIT_MIN_MS, FADE_MS, FLIGHT_MS, HOLD_MS, LIFE_MS, POOL,
} from './fakeChips';
import {
  money2, moneyShort, nextOnline, nextTableBet, pickLauncher, type Seat,
} from './social';
import { pitchY, type Lattice } from './lattice';
import { seatedAt, type Table } from './tables';
import { GAP } from './Board';
// Sound only — no bet, no id, no money. The one thing the fake layer borrows
// from the real game besides geometry.
import { press } from './press';
import { sChipPlaceFaint } from './sound';

/* ═══ the countdown clock ═══════════════════════════════════════════════ */

/** Ornate gold alarm clock with a cream face. Hidden while the camera is up —
 *  the reference removes it entirely for the spin. */
export function Clock({ secs }: { secs: number }) {
  return (
    /* `hot` from five seconds down, so the corner agrees with the big numbers
       arriving in the middle — one event, said in two places. */
    <div className={`rlt-clock ${secs <= 5 ? 'hot' : ''}`} aria-hidden>
      {/* No width/height: social.css sizes this off --rlt-w, because the
          reference dial is 9.03% of the CANVAS and an SVG attribute
          cannot see the canvas. */}
      <svg viewBox="0 0 64 80">
        <defs>
          <radialGradient id="rlt-clk-gold" cx="38%" cy="30%">
            <stop offset="0%" stopColor="#ffeaa8" /><stop offset="52%" stopColor="#d8a944" />
            <stop offset="100%" stopColor="#8a6018" />
          </radialGradient>
          <radialGradient id="rlt-clk-face" cx="40%" cy="32%">
            <stop offset="0%" stopColor="#fffdf3" /><stop offset="100%" stopColor="#e8dcc0" />
          </radialGradient>
        </defs>
        {/* MEASURED off ref1's gold silhouette: the whole clock is 54 x 67 px
            (aspect 0.806), the gold body Ø 50, the cream face Ø 42 with its centre
            43 px below the box top, and the red digits cap 16 px. The old
            "0 0 64 68" viewBox forced aspect 0.94 — 13% too short — and left the
            dial filling only ~78% of the box (body Ø 43, face Ø 33, digits 12.4).
            Rows y317-322 also carry a thin gold ARC spanning the bells: a carry
            handle, where the old markup drew a 6x6 striker rect. */}
        {/* bells — domed: a body gradient plus a bright polished cap each, so
            they read as turned metal rather than flat gold discs */}
        <circle cx="11" cy="17" r="11" fill="url(#rlt-clk-gold)" stroke="#6d4f0c" strokeWidth="1.2" />
        <circle cx="53" cy="17" r="11" fill="url(#rlt-clk-gold)" stroke="#6d4f0c" strokeWidth="1.2" />
        <ellipse cx="8" cy="13" rx="4.4" ry="3.2" fill="#fff6d8" opacity=".75" />
        <ellipse cx="50" cy="13" rx="4.4" ry="3.2" fill="#fff6d8" opacity=".75" />
        <circle cx="11" cy="17" r="3" fill="#8a6018" stroke="#5c400a" strokeWidth=".8" />
        <circle cx="53" cy="17" r="3" fill="#8a6018" stroke="#5c400a" strokeWidth=".8" />
        {/* legs, with rounded shoes */}
        <path d="M18 73 L12 80" stroke="#8a6018" strokeWidth="4" strokeLinecap="round" />
        <path d="M46 73 L52 80" stroke="#8a6018" strokeWidth="4" strokeLinecap="round" />
        <circle cx="11.5" cy="79.4" r="2.6" fill="#6d4f0c" />
        <circle cx="52.5" cy="79.4" r="2.6" fill="#6d4f0c" />
        {/* body + face */}
        <circle cx="32" cy="50" r="29" fill="url(#rlt-clk-gold)" stroke="#6d4f0c" strokeWidth="1.5" />
        {/* the polished sweep across the metal rim */}
        <path d="M10 38 A 29 29 0 0 1 44 23" fill="none" stroke="#fff3c8" strokeWidth="2.4" strokeLinecap="round" opacity=".6" />
        <circle cx="32" cy="50" r="24" fill="url(#rlt-clk-face)" stroke="#a9761d" strokeWidth="1.2" />
        {/* hour ticks — twelve short strokes; the face read as a plain plate
            without them */}
        {Array.from({ length: 12 }, (_, i) => {
          const a = (i * Math.PI) / 6;
          const x1 = 32 + 21.4 * Math.sin(a), y1 = 50 - 21.4 * Math.cos(a);
          const x2 = 32 + 19 * Math.sin(a), y2 = 50 - 19 * Math.cos(a);
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                       stroke="#a9761d" strokeWidth={i % 3 === 0 ? 1.6 : 0.9} strokeLinecap="round" />;
        })}
        <text x="32" y="50" textAnchor="middle" dy="0.36em"
              fontSize="26" fontWeight="900" fill="#9D4622"
              style={{ fontFamily: '"Arial Narrow", Arial, sans-serif' }}>
          {secs}
        </text>
        {/* THE STRIKER, where the front "string" used to be. The arc between
            the bells crossed IN FRONT of them, which no real clock does — the
            thing that actually lives between twin bells is the hammer. A stem
            rising from the body with a ball head, slightly tilted, plus a
            small post under each bell so they read as MOUNTED rather than
            floating. (Owner, 2026-09-01.) */}
        <path d="M14 24 L11 19" stroke="#5c400a" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M50 24 L53 19" stroke="#5c400a" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M32 26 L34 12" stroke="#6d4f0c" strokeWidth="2.4" strokeLinecap="round" />
        <circle cx="34.4" cy="10.5" r="3.4" fill="url(#rlt-clk-gold)" stroke="#5c400a" strokeWidth=".9" />
        <ellipse cx="33.4" cy="9.4" rx="1.3" ry="1" fill="#fff6d8" opacity=".8" />
        {/* one more step of realism: a bezel ring inside the face edge */}
        <circle cx="32" cy="50" r="22.6" fill="none" stroke="#c9b98f" strokeWidth=".8" opacity=".8" />
      </svg>
    </div>
  );
}

/* ═══ the signs ═════════════════════════════════════════════════════════
 *
 * WHY THESE ARE SVG AND NOT STYLED HTML
 * -------------------------------------
 * The owner reported "weird lines" through these glyphs three separate times,
 * and three separate CSS fixes failed. They failed because the artefact is not
 * a styling mistake, it is what the HTML text-effect stack actually does:
 *
 *   - `-webkit-text-stroke` centres the stroke ON the outline, so half of it is
 *     inside the glyph. `paint-order: stroke fill` is supposed to bury that half
 *     under the fill — but the fill was `background-clip: text`, which Chrome
 *     rasterises as a separate clipped layer. The seam between the two layers is
 *     a hairline of stroke colour tracing every letter. That is the line.
 *   - Stacked `drop-shadow()` filters re-rasterise that seam once per filter, so
 *     it got DARKER with each one added trying to fix it.
 *   - And the size could never be right: it was derived from hand-parsed TTF
 *     metrics that disagreed with the browser by 25%.
 *
 * SVG has none of those problems. `fill` takes a real `<linearGradient>` — one
 * layer, no seam. `paintOrder` is honoured on a single painted glyph. The 3-D
 * lift is three copies of the same `<text>` at different y, which is how sign
 * painters have done it for a century and is exact by construction. And the size
 * comes from a viewBox, so it is right at every canvas width with no font
 * arithmetic at all. Nothing is condensed, so every stem carries one weight.
 */

/* ── THE SIGN ENGINE ────────────────────────────────────────────────────
   One component paints every fixed headline in the game: both betting calls and
   both results. Sizes are in the viewBox's own units and the caller sets a
   height in CSS, so a sign is the same weight whatever it says.

   `em` is the string's ink width in ems, MEASURED in this browser with the real
   loaded face (Montserrat Black Italic) — not parsed out of the TTF, which is
   what put the old banner 25% out. Passing it in rather than measuring at
   runtime keeps this a pure render with no layout read in it. */
const SG_FS = 100;
const SG_BASE = 152;            // baseline: caps top out at 82, descenders ~173
const SG_PAD = 34;              // side room for the stroke and the lift
const SG_STROKE = 9;            // ~0.09 em — still reads at quarter scale
const SG_LIFT = 7;              // one step of the 3-D stack

type Skin = {
  fill: [string, string, string]; edge: string; mid: string; deep: string;
  plate?: string[]; lip?: string;
};

function Sign({ id, text, em, skin, className }: {
  id: string; text: string; em: number; skin: Skin; className?: string;
}) {
  const vbW = Math.round(em * SG_FS + SG_PAD * 2);
  const vbH = 216;
  const t = {
    x: vbW / 2, textAnchor: 'middle' as const,
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 900, fontStyle: 'italic' as const, fontSize: SG_FS,
    letterSpacing: -1,
  };
  const stack = (dy: number, c: string, opacity?: number) => (
    <text {...t} y={SG_BASE + dy} fill={c} stroke={c} strokeWidth={SG_STROKE}
          strokeLinejoin="round" paintOrder="stroke" opacity={opacity}>{text}</text>
  );
  return (
    <svg className={className} viewBox={`0 0 ${vbW} ${vbH}`}
         preserveAspectRatio="xMidYMid meet" role="presentation" aria-hidden>
      <defs>
        <linearGradient id={`${id}-ink`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={skin.fill[0]} />
          <stop offset=".56" stopColor={skin.fill[1]} />
          <stop offset="1" stopColor={skin.fill[2]} />
        </linearGradient>
        {skin.plate && (<>
          <linearGradient id={`${id}-plate`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={skin.plate[0]} />
            <stop offset=".22" stopColor={skin.plate[1]} />
            <stop offset=".58" stopColor={skin.plate[2]} />
            <stop offset="1" stopColor={skin.plate[3]} />
          </linearGradient>
          {/* the flare dies away at both ends rather than stopping — a hard edge
              reads as a rectangle lying on the felt, not as light */}
          <linearGradient id={`${id}-fade`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#fff" stopOpacity="0" />
            <stop offset=".16" stopColor="#fff" stopOpacity="1" />
            <stop offset=".84" stopColor="#fff" stopOpacity="1" />
            <stop offset="1" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
          <mask id={`${id}-mk`}>
            <rect x="0" y="0" width={vbW} height={vbH} fill={`url(#${id}-fade)`} />
          </mask>
        </>)}
      </defs>

      {skin.plate && (
        /* THE FLARE. Behind the glyphs and INSIDE their run, so the first and
           last letters break out of it — that overlap is what makes a painted
           sign look painted rather than like a label stuck on a box. */
        <g mask={`url(#${id}-mk)`}>
          <rect x="0" y="86" width={vbW} height="72" fill={`url(#${id}-plate)`} />
          {skin.lip && <rect x="0" y="156" width={vbW} height="5" fill={skin.lip} opacity=".85" />}
        </g>
      )}

      {/* THE 3-D STACK, back to front. Each copy is stroked in its own colour so
          the silhouette stays solid the whole way down the lift. */}
      {stack(SG_LIFT * 3, skin.deep, .55)}
      {stack(SG_LIFT * 2, skin.deep)}
      {stack(SG_LIFT, skin.mid)}
      <text {...t} y={SG_BASE} fill={`url(#${id}-ink)`} stroke={skin.edge}
            strokeWidth={SG_STROKE} strokeLinejoin="round" paintOrder="stroke">{text}</text>
    </svg>
  );
}

type CallKind = 'open' | 'shut';

const CB_TEXT: Record<CallKind, { s: string; em: number }> = {
  open: { s: 'Place Your Bet', em: 8.007 },
  shut: { s: 'Stop Betting', em: 6.857 },
};

/** the two colourways, kept as data so the markup has no branches in it */
const CB_SKIN: Record<CallKind, Skin> = {
  /* betting OPEN — cream glyphs on a gold flare */
  open: {
    fill: ['#FFFDF2', '#FCEFC8', '#F3D093'],
    edge: '#C2571F', mid: '#8B4C23', deep: '#4A2410',
    plate: ['#FFE877', '#FFC12F', '#FF9330', '#E65C14'],
    lip: '#FFE9A8',
  },
  /* betting CLOSED — the same treatment in steel blue. Same shape, different
     temperature: the player should read the STATE before they read the words. */
  shut: {
    fill: ['#F6FBFF', '#CFE2F7', '#93B4E0'],
    edge: '#1E4C8C', mid: '#123761', deep: '#071B34',
    plate: ['#CFE6F5', '#5B8FD6', '#2E5FA8', '#12325A'],
    lip: '#BFDCF5',
  },
};

export function CallBanner({ call }: { call: CallKind | null }) {
  if (!call) return null;
  const { s, em } = CB_TEXT[call];
  return (
    <div className={`rlt-call ${call}`} aria-hidden>
      <Sign id={`cb-${call}`} text={s} em={em} skin={CB_SKIN[call]} className="rlt-call-svg" />
    </div>
  );
}

/* ═══ the result call ═══════════════════════════════════════════════════ */

/* WIN is loud and LOSE is quiet, and that asymmetry is the design.
   A loss that arrives with the same fanfare as a win teaches the player that
   the fanfare means nothing — and a loss that arrives with a big red X is a
   game telling someone off for playing it. So the win gets gold, rays, a
   count-up and a full second of daylight; the loss gets one cool grey line, no
   ornament, and it leaves quickly. The next round is the consolation. */
const RC_WIN: Skin = {
  fill: ['#FFFBE6', '#FFE9A8', '#F0C24E'],
  edge: '#8A5A12', mid: '#5E3A08', deep: '#2E1B02',
};
const RC_LOSE: Skin = {
  fill: ['#EEF5F1', '#C8D8D0', '#9AB0A6'],
  edge: '#31463C', mid: '#1E2E27', deep: '#0D1712',
};

export function ResultCall({ won, amount }: { won: boolean; amount: number }) {
  return (
    <div className={`rlt-banner ${won ? 'win' : 'lose'}`} role="status">
      <div className="rlt-banner-in">
        {won && <span className="rlt-banner-rays" aria-hidden />}
        <Sign id={won ? 'rc-w' : 'rc-l'}
              text={won ? 'YOU WIN' : 'NO WIN'}
              em={won ? 4.953 : 4.289}
              skin={won ? RC_WIN : RC_LOSE}
              className="rlt-banner-sign" />
        {/* THE AMOUNT STAYS HTML. It is the one string here that is not fixed, and
            a Sign needs its ink width up front. Plain text with a solid fill has
            none of the artefacts the old banner had — those came from
            background-clip and text-stroke, neither of which is in play here. */}
        <span className="rlt-banner-amt">
          {won ? `+₹${amount.toLocaleString('en-IN')}` : `−₹${amount.toLocaleString('en-IN')}`}
        </span>
      </div>
    </div>
  );
}

/* ═══ the last five seconds ═════════════════════════════════════════════ */

/**
 * A big numeral in the middle of the table for each of the final five seconds:
 * 5, then 4, then 3, 2, 1 — each one arriving, holding, and leaving before the
 * next — and then "Stop Betting".
 *
 * WHY IT EARNS ITS SPACE. The clock in the corner has had the same number on it
 * the whole time, and a player who is looking at the numbers is not looking at
 * the clock. The last five seconds are the only ones where the exact figure
 * changes what they do, so that is the only stretch where it is worth putting a
 * number where their eyes already are. It is off screen the rest of the round.
 *
 * IT ESCALATES, in three registers at once, because one is easy to miss:
 *   - COLOUR climbs from gold through amber to a hot orange-red.
 *   - SIZE grows a little with each number.
 *   - PITCH rises (see sCountPip) at three.
 * None of them shouts. This runs every twenty seconds for as long as the player
 * is at the table, and anything that shouts on that cycle becomes something to
 * be rid of rather than something to respond to.
 *
 * The ring is a stroked circle that expands and fades — the shape of a timer
 * running out, drawn once per second rather than continuously, so there is no
 * animation running through the rest of the round.
 */
const CD_SKIN: Record<number, Skin & { ring: string }> = {
  5: { fill: ['#FFFDF0', '#FFE9A8', '#EFC356'], edge: '#8A5A12', mid: '#5E3A08', deep: '#2E1B02', ring: '#FFD979' },
  4: { fill: ['#FFF8E4', '#FFDD8E', '#E9A93A'], edge: '#8A4A0E', mid: '#5C2F06', deep: '#2C1502', ring: '#FFC861' },
  3: { fill: ['#FFF2E0', '#FFC98A', '#E8892C'], edge: '#8A3A0B', mid: '#5C2405', deep: '#2C1002', ring: '#FFA04A' },
  2: { fill: ['#FFEDE2', '#FFB587', '#E4692A'], edge: '#872C08', mid: '#5A1B04', deep: '#2A0C01', ring: '#FF8A45' },
  1: { fill: ['#FFE9E2', '#FF9E85', '#DC4A2C'], edge: '#821D06', mid: '#561203', deep: '#280800', ring: '#FF6B3D' },
};

export function CountIn({ n, onPip }: { n: number; onPip: (n: number) => void }) {
  const sk = CD_SKIN[n];
  useEffect(() => { if (!document.hidden) onPip(n); }, [n, onPip]);
  if (!sk) return null;

  const id = `cd${n}`;
  const t = {
    x: 110, y: 172, textAnchor: 'middle' as const,
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 900, fontStyle: 'italic' as const, fontSize: 168,
  };
  return (
    /* keyed by n in Page.tsx, so React REMOUNTS this for every number and the
       entrance keyframe replays. Re-rendering the same node would not. */
    <div className={`rlt-count rlt-count--${n}`} aria-hidden>
      <svg viewBox="0 0 220 220" preserveAspectRatio="xMidYMid meet" role="presentation">
        <defs>
          <linearGradient id={`${id}-ink`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={sk.fill[0]} />
            <stop offset=".56" stopColor={sk.fill[1]} />
            <stop offset="1" stopColor={sk.fill[2]} />
          </linearGradient>
        </defs>
        <circle className="rlt-count-ring" cx="110" cy="110" r="82"
                fill="none" stroke={sk.ring} strokeWidth="5" />
        <text {...t} y={172 + 18} fill={sk.deep} stroke={sk.deep} strokeWidth={13}
              strokeLinejoin="round" paintOrder="stroke" opacity=".55">{n}</text>
        <text {...t} y={172 + 12} fill={sk.deep} stroke={sk.deep} strokeWidth={13}
              strokeLinejoin="round" paintOrder="stroke">{n}</text>
        <text {...t} y={172 + 6} fill={sk.mid} stroke={sk.mid} strokeWidth={13}
              strokeLinejoin="round" paintOrder="stroke">{n}</text>
        <text {...t} fill={`url(#${id}-ink)`} stroke={sk.edge} strokeWidth={13}
              strokeLinejoin="round" paintOrder="stroke">{n}</text>
      </svg>
    </div>
  );
}

/* ═══ the leaderboard ═══════════════════════════════════════════════════ */

/* ── the portraits ──────────────────────────────────────────────────────
   Drawn, not loaded. An <img> pointing at /games/roulette/faces/*.png rendered
   an empty disc for every seat, because those files do not exist — and a
   missing portrait is worse than a drawn one, since the entire job of this
   layer is to make the table look occupied.

   WHAT CHANGED. The old one was a hue-rotated blob: same silhouette every time,
   same haircut, colour picked by `seed * 47 + 190` so half the roster came out
   in the same teal as the felt they were sitting on. The owner's word for it
   was "really bad", and it was.

   These are built the way a real avatar set is: a fixed palette of four skin
   tones and five hair colours that actually occur, eight distinct silhouettes,
   and a garment tone chosen to sit AGAINST the felt rather than in it. The seed
   is the player's index in FAKE_NAMES, so a given name always wears the same
   face — an avatar that changes hair between rounds is the loudest tell the
   whole layer has. */

const SKIN = ['#F2CDA7', '#E0AE7E', '#C08A57', '#96603A'] as const;
const SKIN_SH = ['#DCB088', '#C4905F', '#A06E3F', '#7A4826'] as const;
const HAIR = ['#1E1712', '#33210F', '#4E3118', '#6B4423', '#141420'] as const;
/* deliberately warm/violet/berry — nothing in the felt's green-teal family, so
   every portrait separates from the table at a glance */
const WEAR = ['#C0392B', '#8E44AD', '#2C3E93', '#B8860B', '#146A5E', '#A0325C',
              '#3C6E1F', '#B04A18'] as const;

/** eight silhouettes, indexed by seed % 8 */
function hairPath(style: number): React.ReactNode {
  switch (style) {
    case 0: // long, falling either side
      return (<>
        <path d="M14 30c-2-14 5-21 18-21s20 7 18 21c-1-8-4-11-6-12-4 2-20 3-24 0-3 1-5 4-6 12Z" />
        <path d="M13 28c-1 9 0 16 1 20l5-1c-2-6-3-13-2-19Zm38 0c1 9 0 16-1 20l-5-1c2-6 3-13 2-19Z" />
      </>);
    case 1: // top knot
      return (<>
        <circle cx="32" cy="8" r="6" />
        <path d="M15 29c-1-13 6-19 17-19s18 6 17 19c-2-7-5-10-7-11-4 2-16 2-20 0-2 1-5 4-7 11Z" />
      </>);
    case 2: // short crop
      return <path d="M16 30c-1-14 6-20 16-20s17 6 16 20c-2-8-5-11-8-12-3 2-13 2-16 0-3 1-6 4-8 12Z" />;
    case 3: // curls
      return (<>
        <path d="M16 30c-1-14 6-20 16-20s17 6 16 20c-2-8-5-11-8-12-3 2-13 2-16 0-3 1-6 4-8 12Z" />
        <circle cx="19" cy="17" r="5" /><circle cx="27" cy="12" r="5.6" />
        <circle cx="36" cy="12" r="5.6" /><circle cx="45" cy="17" r="5" />
      </>);
    case 4: // beanie
      return (<>
        <path d="M15 26c0-11 8-17 17-17s17 6 17 17Z" />
        <rect x="13" y="25" width="38" height="6" rx="3" />
      </>);
    case 5: // fade
      return <path d="M18 28c0-11 5-16 14-16s14 5 14 16c-2-6-4-8-7-9-3 1-11 1-14 0-3 1-5 3-7 9Z" />;
    case 6: // ponytail
      return (<>
        <path d="M16 30c-1-14 6-20 16-20s17 6 16 20c-2-8-5-11-8-12-3 2-13 2-16 0-3 1-6 4-8 12Z" />
        <path d="M47 20c6 1 9 7 8 14-1 6-4 9-7 10 3-6 3-13 0-18Z" />
      </>);
    default: // bob
      return (<>
        <path d="M14 31c-2-15 6-22 18-22s20 7 18 22c-1-9-4-12-6-13-4 2-20 3-24 0-3 1-5 4-6 13Z" />
        <path d="M13 29c-1 8-1 13 0 17h6c-2-5-3-11-2-17Zm38 0c1 8 1 13 0 17h-6c2-5 3-11 2-17Z" />
      </>);
  }
}

export function Face({ seed }: { seed: number }) {
  const skin = SKIN[seed % SKIN.length];
  const shade = SKIN_SH[seed % SKIN_SH.length];
  const hair = HAIR[(seed * 3) % HAIR.length];
  const wear = WEAR[seed % WEAR.length];
  const style = seed % 8;
  const cid = `rf${seed}`;
  return (
    <svg viewBox="0 0 64 64" className="rlt-face" aria-hidden>
      <defs>
        <clipPath id={cid}><circle cx="32" cy="32" r="32" /></clipPath>
        <linearGradient id={`${cid}b`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2C3742" /><stop offset="1" stopColor="#161C24" />
        </linearGradient>
      </defs>
      <g clipPath={`url(#${cid})`}>
        <rect width="64" height="64" fill={`url(#${cid}b)`} />
        {/* the light in the room, so the head has somewhere to be lit from */}
        <circle cx="24" cy="18" r="30" fill="#fff" opacity=".07" />
        {/* shoulders */}
        <path d="M4 64c1.5-12 12-18.5 28-18.5S58.5 52 60 64Z" fill={wear} />
        <path d="M4 64c1.5-12 12-18.5 28-18.5V64Z" fill="#000" opacity=".13" />
        {/* collar */}
        <path d="M26 46.5 32 55l6-8.5-6-2.5Z" fill="#fff" opacity=".16" />
        {/* neck */}
        <path d="M26 37h12v9c0 3-12 3-12 0Z" fill={shade} />
        {/* head */}
        <ellipse cx="32" cy="27" rx="15" ry="17" fill={skin} />
        <ellipse cx="17.5" cy="29" rx="3" ry="4" fill={skin} />
        <ellipse cx="46.5" cy="29" rx="3" ry="4" fill={skin} />
        {/* the shaded side — one light source, from the upper left */}
        <path d="M32 10c8.3 0 15 7.6 15 17s-6.7 17-15 17Z" fill="#000" opacity=".10" />
        {/* eyes and mouth. Small, dark, and slightly wide-set: at 56 px this is
            all that is needed for a face to read as looking at you. */}
        <ellipse cx="26" cy="28" rx="1.9" ry="2.3" fill="#1B1410" />
        <ellipse cx="38" cy="28" rx="1.9" ry="2.3" fill="#1B1410" />
        <path d="M27.5 35.5c1.6 2 7.4 2 9 0" fill="none" stroke="#8A4A3C"
              strokeWidth="1.6" strokeLinecap="round" />
        <g fill={hair}>{hairPath(style)}</g>
      </g>
    </svg>
  );
}

/* ── the tiles ──────────────────────────────────────────────────────────
   Absolutely positioned by RANK with a transition on `top`, keyed by name.
   That is what makes the re-sort visible: React keeps the node when the array
   reorders, and the node slides to its new slot. A flex column would jump. */

const RANK_LABEL = ['1st', '2nd', '3rd'] as const;

export function Leaderboard({ seats, gains, running, target, frozen, table, windowMs, you }: {
  seats: Seat[];
  gains: Record<string, number>;
  running: boolean; target: number; frozen: boolean;
  /** the joined room — the crowd numbers must AGREE with what its lobby card
   *  advertised, or the player catches the room lying about itself */
  table?: Table; windowMs?: number;
  /** the REAL player's round, shown only when they beat the whole cast:
   *  name/photo from the profile, win = this round's gross. */
  you?: { name: string; photo: string | null; win: number } | null;
}) {
  /* The crowd tile blinks as each volley of chips leaves it — the one cue
     that was missing from "the coins come from all those players". The
     emitter announces volleys on a DOM event; no money crosses this seam. */
  const crowdRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const on = () => {
      const el = crowdRef.current; if (!el) return;
      el.classList.remove('volley');
      void el.offsetWidth;   // restart the animation (one reflow per ~500ms, cheap)
      el.classList.add('volley');
    };
    window.addEventListener('rlt-volley', on);
    return () => window.removeEventListener('rlt-volley', on);
  }, []);
  const topGain = Math.max(0, ...Object.values(gains));
  const youOnTop = !!you && you.win > 0 && you.win > topGain;
  return (
    <div className="rlt-lb" aria-hidden>
      {/* THE PLAYER, CROWNED — only when their round beat every named seat's.
          It rides ABOVE rank 1 for the result hold, so the message is exactly
          the owner's: "yes, I am also ranking on top". */}
      {youOnTop && (
        <div className="rlt-lb-you">
          <span className="rlt-lb-disc rlt-lb-disc--you">
            {you!.photo
              ? <img className="rlt-lb-photo" src={you!.photo} alt="" />
              : <span className="rlt-lb-youinit">{(you!.name || 'You').slice(0, 1).toUpperCase()}</span>}
            <span className="rlt-lb-medal rlt-lb-medal--you">★</span>
          </span>
          <span className="rlt-lb-name rlt-lb-name--you">You</span>
          <span className="rlt-lb-gain" key={`y${you!.win}`}>+{money2(you!.win)}</span>
        </div>
      )}
      {seats.map((s, rank) => (
        <div className={`rlt-lb-seat rlt-lb-seat--r${rank}`} key={s.name}
             style={{ top: `calc(${rank} * var(--rlt-lb-pitch))` }}>
          <span className="rlt-lb-disc">
            {s.photo
              ? <img className="rlt-lb-photo" src={s.photo} alt="" loading="lazy"
                     onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
              : <Face seed={s.face} />}
            <span className="rlt-lb-medal">{RANK_LABEL[rank]}</span>
            {gains[s.name] > 0 && <span className="rlt-lb-pop" key={s.win} />}
          </span>
          <span className="rlt-lb-name">{s.name}</span>
          <span className="rlt-lb-win">{moneyShort(s.win)}</span>
          {gains[s.name] > 0 && (
            <span className="rlt-lb-gain" key={`g${s.win}`}>+{money2(gains[s.name])}</span>
          )}
        </div>
      ))}

      {/* ── EVERYBODY ELSE ──────────────────────────────────────────────
          The fourth tile, and the one that does the real work. Three named
          players are three people; this is the other four thousand, and it is
          where nearly every chip on the felt comes from. It carries the online
          count — which used to float on its own in the corner meaning nothing —
          and the table's running total, so the two numbers that describe the
          crowd finally sit on the crowd. */}
      <div className="rlt-lb-crowd" ref={crowdRef} style={{ top: 'calc(3 * var(--rlt-lb-pitch))' }}>
        <span className="rlt-lb-heap">
          <span className="rlt-lb-mini rlt-lb-mini--a"><Face seed={4} /></span>
          <span className="rlt-lb-mini rlt-lb-mini--b"><Face seed={9} /></span>
          <span className="rlt-lb-mini rlt-lb-mini--c"><Face seed={5} /></span>
        </span>
        <span className="rlt-lb-crowd-k">ALL PLAYERS</span>
        <OnlineTicker table={table} />
        <TableTotal running={running} target={target} frozen={frozen}
                    table={table} windowMs={windowMs} />
      </div>
    </div>
  );
}

/* ═══ the two live counters ════════════════════════════════════════════
   Both write straight to a ref'd node on a timer. NEITHER is React state: the
   online count moves every ~3 s and the table total every 100 ms, and putting
   either into state would re-render the whole board 10x a second while the
   wheel is mid-spin. */

function OnlineTicker({ table }: { table?: Table }) {
  const ref = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    /* THE COUNT IS THE ROOM'S COUNT. The lobby card said "470 playing"; this
       tile used to say ~4,300 at the same table, and a player who reads both
       has just caught the room lying about itself (2026-08-16 audit, C8). The
       base now comes from the same seatedAt() the lobby card renders, so the
       two can drift by breathing-room noise but never disagree in size. The
       4,300 figure survives only as the fallback for a table-less mount. */
    const seatBase = () => (table ? seatedAt(table, Date.now()) : 4300);
    let base = seatBase();
    let n = Math.max(6, base + Math.floor(Math.random() * Math.max(4, base * 0.04)) - Math.max(2, base * 0.02));
    let t = 0;
    const tick = () => {
      base = seatBase() + (Math.random() - 0.5) * Math.max(2, base * 0.002);
      n = nextOnline(n, base, Math.random);
      if (ref.current) ref.current.textContent = n.toLocaleString('en-IN');
      t = window.setTimeout(tick, 2500 + Math.random() * 2000);
    };
    tick();
    return () => window.clearTimeout(t);
  }, [table]);
  /* The initial text is captured ONCE. Rendered live, the JSX child changed
     every time the virtual round ticked over, and React's diff then overwrote
     the count the effect had been walking -- the number visibly snapped back
     every ~27s. A state initializer runs once, so React's vdom never changes
     and the ref writes own the node. (Recheck P8.) */
  const [initial] = useState(() => (table ? seatedAt(table, Date.now()) : 4300).toLocaleString('en-IN'));
  return <span ref={ref} className="rlt-lb-crowd-n">{initial}</span>;
}

/* ═══ the Record rail ═══════════════════════════════════════════════════ */

/** Ornate plaque over the last results, newest on TOP in a gold ring.
 *  ZERO IS GREY, not green — §18 corrects that from the reference crops. */
export function RecordRail({ history, colourOf, onMyBets }: {
  history: number[];
  colourOf: (n: number) => 'red' | 'black' | 'green';
  /** opens the player's own history — see the note on the footer below */
  onMyBets?: () => void;
}) {
  const six = history.slice(0, 6);
  return (
    /* aria-hidden moved OFF the wrapper and onto the decorative parts: the rail
       now contains a real control, and a button inside an aria-hidden subtree is
       invisible to a screen reader while still being focusable — the worst of
       both. */
    <div className="rlt-record">
      <span className="rlt-record-plate" aria-hidden>Record</span>
      <div className="rlt-record-track" aria-hidden>
        {six.map((n, i) => (
          <span key={`${i}-${n}`}
                className={`rlt-record-n rlt-record-n--${n === 0 ? 'zero' : colourOf(n)} ${i === 0 ? 'top' : ''}`}>
            {n}
          </span>
        ))}
      </div>
      {/* THE TABLE'S RESULTS ARE RIGHT THERE; THE PLAYER'S OWN WERE TWO TAPS AWAY
          in the menu. This is where a player is already looking when they think
          about history, so the door to their own record belongs here — same
          panel, same language, just a hairline rule and a line of gold so it
          reads as a footer to the list rather than a separate control. */}
      {onMyBets && (
        <button type="button" className="rlt-record-mine" {...press(onMyBets)}>
          My bets
        </button>
      )}
    </div>
  );
}

/* ═══ the table's running total ═════════════════════════════════════════ */

/**
 * FAKE ONLY. This never reads the player's `totalBet` — the firewall grows no
 * door. The player's own stake is shown by a separate real pill beside it, so
 * hiding their money is not the price of the effect.
 */
function TableTotal({ running, target, frozen, table, windowMs }: {
  running: boolean; target: number; frozen: boolean;
  table?: Table; windowMs?: number;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const val = useRef(0);
  const t0 = useRef(0);

  useEffect(() => {
    if (!running) return;
    t0.current = performance.now();
    val.current = 0;
    /* The pot fills over THIS room's window, not a hardwired 20s -- in a quick
       room the old curve flatlined with 8 seconds still on the clock (audit
       m14). And it is sized to THIS room's crowd: the target was tuned when
       the tile claimed ~4,300 players, so a 470-seat room scales it down --
       roughly the same rupees-per-head either way. */
    const winMs = windowMs ?? 20_000;
    const scaled = table ? Math.max(1500, target * (seatedAt(table, Date.now()) / 4300)) : target;
    let t = 0;
    const tick = () => {
      const u = Math.min(1, (performance.now() - t0.current) / winMs);
      val.current = nextTableBet(val.current, u, scaled, Math.random);
      if (ref.current) ref.current.textContent = moneyShort(val.current);
      if (u < 1) t = window.setTimeout(tick, 100);
    };
    tick();
    return () => window.clearTimeout(t);
  }, [running, target]);

  // Freezes at the close of betting and stays on screen through the result —
  // it does not clear until the next round arms (§18).
  return <span ref={ref} className={`rlt-lb-crowd-v ${frozen ? 'frozen' : ''}`}>₹0</span>;
}

/* ═══ the bombardment ═══════════════════════════════════════════════════ */

/**
 * F4 — a SIBLING layer. Mounted next to Overlay inside the grid, never through
 * Overlay's `chips` prop, so a fake can never be one .concat() from a real one.
 * F5 — no React state: a 24-node pool in a ref, written straight to the DOM.
 *
 * Everything is compositor-only. Two style writes per chip (spawn transform,
 * then landing transform) with a CSS transition on transform/opacity — never a
 * rAF, never a getBoundingClientRect, never an elementFromPoint. The wheel is
 * animating at the same time and this must not cost it a frame.
 */
export function FakeChipLayer({ active, lattice, railL, railR }: {
  active: boolean; lattice: Lattice | null; railL: number; railR: number;
}) {
  const gridH = lattice ? 13 * pitchY(lattice) : 0;
  const hostRef = useRef<HTMLDivElement | null>(null);
  const poolRef = useRef<HTMLElement[]>([]);
  const freeRef = useRef<number[]>([]);

  const targets = useMemo(
    () => (lattice ? buildTargets(lattice, railL, railR) : []),
    [lattice, railL, railR],
  );

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    // build the pool once
    if (!poolRef.current.length) {
      for (let i = 0; i < POOL; i++) {
        const el = document.createElement('span');
        el.className = 'rlt-fake';
        el.innerHTML = '<span class="rlt-fake-face"></span>';
        el.style.opacity = '0';
        host.appendChild(el);
        poolRef.current.push(el);
        freeRef.current.push(i);
      }
    }
  }, []);

  useEffect(() => {
    if (!active || !targets.length) return;
    let timer = 0;
    let stopped = false;

    const emit = () => {
      if (stopped) return;
      // A hidden tab SKIPS rather than queues: queueing would dump a hundred
      // chips onto the felt the moment the player came back.
      if (!document.hidden) {
        // 2, 3 or 4 — see POOL/EMIT_* in fakeChips.ts for why the table has to
        // look this busy. Weighted low so the stream still has texture rather
        // than arriving in even clumps.
        const r = Math.random();
        const n = r < 0.45 ? 2 : r < 0.82 ? 3 : 4;
        for (let i = 0; i < n; i++) fire();
        // tell the crowd tile a volley just left it (presentation-only event)
        window.dispatchEvent(new Event('rlt-volley'));
      }
      timer = window.setTimeout(emit, EMIT_MIN_MS + Math.random() * (EMIT_MAX_MS - EMIT_MIN_MS));
    };

    /* Chips armed this tick, waiting for the next frame to be released. See
       fire() for why they are not released immediately. */
    const armed: { el: HTMLElement; to: FakeTarget }[] = [];

    /* Chips currently in the air. Each one has three things that must happen to
       it on a schedule — land (sound), start fading, return to the pool — and
       each USED TO BE ITS OWN setTimeout. At 2-4 chips every 42-78 ms that was
       about 150 live timers a second, every one of them an independent task the
       browser has to store, sort into the timer heap and wake the main thread
       for. The wake-ups are the expensive part: they arrive at arbitrary moments
       between frames, so each one can interrupt the wheel mid-animation.
       One rAF-driven sweeper replaces all of them. It runs only while something
       is actually flying, it wakes ONCE per frame instead of ~150 times a second
       at random offsets, and the work it does is the same three state changes at
       the same three deadlines — checked against the chip's own age, so the
       timing is identical to the timers it replaces. */
    type Live = { el: HTMLElement; idx: number; born: number;
                  sounded: boolean; faded: boolean };
    const live: Live[] = [];
    let loopRaf = 0;

    const loop = () => {
      loopRaf = 0;

      // (a) release everything armed on the previous frame. The frame boundary
      //     between the two transforms is what replaced the forced layout.
      if (armed.length) {
        for (const { el, to } of armed) {
          el.style.transition = `transform ${FLIGHT_MS}ms cubic-bezier(.25,.7,.35,1), opacity 120ms linear`;
          el.style.transform = `translate3d(${to.x}px, ${to.y}px, 0) scale(1)`;
          el.style.opacity = '';   // back to the stylesheet's faint default
        }
        armed.length = 0;
      }

      // (b) sweep the deadlines. Backwards so splice() is safe.
      const now = performance.now();
      for (let i = live.length - 1; i >= 0; i--) {
        const c = live[i];
        const age = now - c.born;

        // The chip's voice, as it lands. Token-bucketed and quiet: this fires up
        // to ~15x a second and must sit UNDER the player's own placement sound,
        // never compete with it.
        if (!c.sounded && age >= FLIGHT_MS) {
          c.sounded = true;
          if (!document.hidden) sChipPlaceFaint();
        }
        if (!c.faded && age >= FLIGHT_MS + HOLD_MS) {
          c.faded = true;
          c.el.style.transition = `opacity ${FADE_MS}ms linear`;
          c.el.style.opacity = '0';
        }
        if (age >= LIFE_MS + 40) {
          c.el.style.willChange = '';   // done flying — give the layer back
          freeRef.current.push(c.idx);
          live.splice(i, 1);
        }
      }

      if (armed.length || live.length) loopRaf = requestAnimationFrame(loop);
    };

    /* Backgrounding the app stops rAF, which would leave whatever was mid-flight
       frozen on the felt still holding its compositor layer — the exact thing
       fix (4) exists to prevent. So when we go hidden, land everything at once:
       hand the layers back and return the chips to the pool. emit() already
       skips while hidden, so the felt is quiet either way, and coming back finds
       a full pool rather than one drained by chips that never finished. */
    const onHide = () => {
      if (!document.hidden) return;
      for (const c of live) {
        c.el.style.willChange = '';
        c.el.style.opacity = '0';
        freeRef.current.push(c.idx);
      }
      live.length = 0;
      armed.length = 0;
    };
    document.addEventListener('visibilitychange', onHide);

    const fire = () => {
      const idx = freeRef.current.pop();
      if (idx == null) return;                       // pool exhausted; skip
      const el = poolRef.current[idx];
      const to = pickTarget(targets, Math.random);
      const from = launchPoint(railL, gridH, pickLauncher(Math.random), Math.random);
      const denom = pickDenom(Math.random);

      el.dataset.v = String(denom);
      (el.firstElementChild as HTMLElement).textContent =
        denom >= 1000 ? `${denom / 1000}k` : String(denom);

      // PROMOTED ONLY WHILE IT IS FLYING. This used to be `will-change:
      // transform, opacity` in the stylesheet, which meant all 72 pooled nodes
      // held their own GPU layer permanently — including the ~22 that are idle at
      // any moment and every one of them between rounds. The browser can never
      // release a layer while will-change is set, so the game sat on 72 textures
      // of nine-gradient chip art for its whole life.
      el.style.willChange = 'transform, opacity';

      el.style.transition = 'none';
      el.style.transform = `translate3d(${from.x}px, ${from.y}px, 0) scale(.55)`;
      /* Visible AT the launch point. It was opacity 0 here with a 120ms fade
         after release — by the time a chip could be seen it was already well
         clear of the tile, which is why the crowd never quite read as the
         source (owner, 2026-09-01). Now it pops in ON the tile, small, and
         grows as it flies. */
      el.style.opacity = '0.95';

      // NO `void el.offsetWidth` HERE — that line was fifty FORCED SYNCHRONOUS
      // LAYOUTS a second. Reading offsetWidth makes the browser flush style and
      // layout for the whole document there and then, and at 2-4 chips every
      // 42-78 ms it was doing that ~50 times a second, on the main thread, while
      // the wheel was trying to animate. It is the single biggest cause of the
      // lag on mid-range Android.
      //
      // A frame boundary separates the two states just as well: the start
      // transform is committed in this frame, the end transform in the next, and
      // the transition runs from one to the other exactly as before. The chip
      // sits at its launch point for one extra frame out of a 380 ms flight —
      // 4% of the way, at the point where it is still invisible (opacity 0).
      armed.push({ el, to });
      // born is stamped HERE, not at release, so every deadline is measured from
      // exactly the moment the old setTimeout(...) calls would have been armed.
      live.push({ el, idx, born: performance.now(), sounded: false, faded: false });
      if (!loopRaf) loopRaf = requestAnimationFrame(loop);
    };

    emit();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onHide);
      if (loopRaf) cancelAnimationFrame(loopRaf);
      // Hand every in-flight chip back before tearing down, so a remount starts
      // with a full pool rather than one drained by chips that will never land.
      for (const c of live) { c.el.style.willChange = ''; }
      live.length = 0;
      armed.length = 0;
      poolRef.current.forEach((el) => {
        el.style.transition = 'none';
        el.style.opacity = '0';
        el.style.willChange = '';   // hand every layer back
      });
      freeRef.current = poolRef.current.map((_, i) => i);
    };
  }, [active, targets, gridH, railL]);

  // display:none while inactive, so by construction nothing here is animating
  // while the ball is running. Verify it, do not assume it.
  /* ── THE ORIGIN SHIFT, AND WHY IT IS ON THE LAYER ─────────────────────
     `inset: 0` puts this layer's box on the GRID, whose left edge is the left
     rail's left edge. Every coordinate it draws — buildTargets, launchPoint,
     linePoints, cellRect — is LATTICE-local, and the lattice's x = 0 is number
     column 0, one whole rail further right. Overlay.tsx:66 has carried
     `originX = railL + GAP` for exactly this reason since it was written.

     This layer never had it. So every crowd chip was placed one rail-width —
     one whole column, 57.6 px at a 430 px canvas — to the LEFT of its target:
     straight-ups landed a column over, the dozens landed on the numbers, the
     even-money chips fell off the board past the rail, and the launch point was
     57.6 px left of the faces it was supposed to be leaving. That is both of the
     things the owner reported: "it is still settling in a wrong position" and
     "the coins do not look like they are coming from the profiles".

     It is applied ONCE, here, on the layer, rather than added to x in fire().
     Two additions in one function is a thing that can be done half — this
     cannot. */
  return (
    <div ref={hostRef} className="rlt-fakelayer" aria-hidden
         style={{
           display: active ? 'block' : 'none',
           transform: `translateX(${railL + GAP}px)`,
         }} />
  );
}
